import {
  CreateDateColumn,
  UpdateDateColumn,
  Entity,
  Index,
  Column,
  RelationId,
  ManyToOne,
  PrimaryGeneratedColumn
} from 'typeorm'
import { ObjectType, Field, ID, Int } from 'type-graphql'
import { Domain } from '@things-factory/shell'
import { WorkflowExecution } from './workflow-execution.js'
import { WorkflowStepType } from '../types/workflow-types.js'

/**
 * WorkflowExecutionStep Entity
 *
 * Represents a single step within a workflow execution.
 * Tracks step status, timing, output, and errors.
 */
@ObjectType({
  description: 'Entity representing a single step in a workflow execution'
})
@Entity('workflow_execution_steps')
@Index('ix_workflow_execution_step_0', ['domain', 'execution', 'status'], {
  where: '"deleted_at" IS NULL'
})
@Index('ix_workflow_execution_step_1', ['domain', 'execution', 'order'], {
  where: '"deleted_at" IS NULL'
})
export class WorkflowExecutionStep {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID, { description: 'Unique step execution identifier' })
  id: string

  @ManyToOne(type => Domain)
  @Field(type => Domain, { nullable: true, description: 'Domain to which this step belongs' })
  domain?: Domain

  @RelationId((step: WorkflowExecutionStep) => step.domain)
  domainId?: string

  @ManyToOne(type => WorkflowExecution, execution => execution.steps)
  @Field(type => WorkflowExecution, { description: 'Parent execution' })
  execution: WorkflowExecution

  @RelationId((step: WorkflowExecutionStep) => step.execution)
  @Field({ description: 'Execution ID' })
  executionId: string

  @Column()
  @Field({ description: 'Step name' })
  stepName: string

  @Column({ type: 'varchar', length: 50 })
  @Field(type => String, { description: 'Step type' })
  stepType: WorkflowStepType

  @Column({ type: 'int' })
  @Field(type => Int, { description: 'Step order in the workflow' })
  order: number

  @Column({ type: 'varchar', length: 50 })
  @Field({ description: 'Step status: pending, running, completed, failed, skipped' })
  status: string

  @Column({ type: 'text', nullable: true })
  @Field({ nullable: true, description: 'Step output data as JSON' })
  output?: string

  @Column({ type: 'text', nullable: true })
  @Field({ nullable: true, description: 'Error message if failed' })
  error?: string

  @Column({ type: 'timestamptz', nullable: true })
  @Field({ nullable: true, description: 'Step start time' })
  startedAt?: Date

  @Column({ type: 'timestamptz', nullable: true })
  @Field({ nullable: true, description: 'Step completion time' })
  completedAt?: Date

  @Column({ type: 'int', nullable: true })
  @Field(type => Int, { nullable: true, description: 'Step duration in milliseconds' })
  durationMs?: number

  @CreateDateColumn({ type: 'timestamptz' })
  @Field({ description: 'Creation timestamp' })
  createdAt: Date

  @UpdateDateColumn({ type: 'timestamptz' })
  @Field({ description: 'Last update timestamp' })
  updatedAt: Date

  @Column({ type: 'timestamptz', nullable: true })
  @Field({ nullable: true, description: 'Soft delete timestamp' })
  deletedAt?: Date
}
